import {gql} from '@apollo/client';

export const GET_SCHEDULED_NOTIFICATIONS = gql`
  query GetScheduledNotifications {
    getScheduledNotifications {
      id
      type
      title
      body
      isRead
      isActive
      status
      category
      createdAt
    }
  }
`;

export const GET_OFFICIAL_EMAILS = gql`
  query GetOfficialEmails {
    getOfficialEmails {
      email
    }
  }
`;

export const NOTIFICATION_BATCH_QUERY = gql`
  query GetAllGroups {
    getAllGroups {
      ... on Error {
        message
        statusCode
      }
      ... on GroupList {
        groups {
          id
          name
          description
          bannerImg
        }
      }
    }
    getOfficialEmails {
      email
    }
    getAllCollections {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithCollections {
        data {
          collections {
            id
            name
            iconUrl
          }
        }
      }
    }
    getAllCampaigns {
      ... on Error {
        message
        statusCode
      }
      ... on Campaigns {
        campaigns {
          id
          fundraisingCompany {
            id
            companyId
            legalName
            logoImgURL
            companyName
          }
          listingType
          campaignStatus
          campaignMode
          offeringType
          campaignMode
        }
      }
    }
    getAllTopics {
      ... on Error {
        message
        statusCode
      }
      ... on Topic {
        id
        icon
        name
      }
    }
    getAllCompanies {
      ... on Error {
        message
        statusCode
      }
      ... on Company {
        id
        name
        logoImgURL
      }
    }
    getAllReports {
      ... on Error {
        message
        statusCode
      }
      ... on Report {
        id
        type
        document
        title
      }
    }
  }
`;

export const GET_NOTIFICATION_CLASSES = gql`
  query GetNotificationClasses($input: NotificationClassFilterInput) {
    getNotificationClasses(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithNotificationClasses {
        message
        data {
          id
          name
          channels {
            channel
            mandatory
          }
          key
          active
        }
        pagination {
          page
          limit
          pages
          total
        }
      }
    }
  }
`;

export const GET_USER_NOTIFICATION_PREFERENCE = gql`
  query GetUserNotificationPreferences(
    $input: NotificationPreferenceFilterInput
  ) {
    getUserNotificationPreferences(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithAnything {
        message
        data
      }
    }
  }
`;

/*
 * Portfolio holdings audience (notification-service, through the gateway).
 * Picker, preview and export need NOTIFICATION or NOTIFICATION_SCHEDULE
 * `pending` CREATE; export needs NOTIFICATION_SCHEDULE EXPORT and emails the
 * CSV to the signed-in admin; snapshots need NOTIFICATION_SCHEDULE `sent`
 * VIEW. Holder counts are cached up to five minutes and are guidance only.
 */
export const HOLDINGS_AUDIENCE_ASSET_CLASSES = gql`
  query HoldingsAudienceAssetClasses($includeUnsettled: Boolean) {
    adminHoldingsAudienceAssetClasses(includeUnsettled: $includeUnsettled) {
      __typename
      ... on ResponseWithHoldingsAudienceAssetClasses {
        data {
          key
          label
          holderCount
        }
        asOf
        stale
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
`;

// `assetClasses: ["COLLECTIONS"]` (exactly) lists collections instead of
// instruments. Sorted by holderCount, only held rows, `limit` at most 100.
export const HOLDINGS_AUDIENCE_INSTRUMENTS = gql`
  query HoldingsAudienceInstruments($input: HoldingsAudienceInstrumentsInput) {
    adminHoldingsAudienceInstruments(input: $input) {
      __typename
      ... on ResponseWithHoldingsAudienceInstruments {
        data {
          id
          kind
          ticker
          name
          exchange
          assetClass
          holderCount
        }
        pagination {
          page
          limit
          total
          pages
          hasNextPage
        }
        asOf
        stale
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
`;

export const PREVIEW_HOLDINGS_AUDIENCE = gql`
  query PreviewHoldingsAudience($input: HoldingsAudiencePreviewInput!) {
    previewHoldingsAudience(input: $input) {
      __typename
      ... on ResponseWithHoldingsAudiencePreview {
        data {
          estimatedRecipients
          holderCount
          sample {
            id
            firstName
            lastNameInitial
            email
            country
            language
          }
          asOf
          stale
          exact
          notApplied
          instrumentTargeted
          message
        }
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
`;

export const EXPORT_HOLDINGS_AUDIENCE = gql`
  mutation ExportHoldingsAudience($input: HoldingsAudiencePreviewInput!) {
    exportHoldingsAudience(input: $input) {
      __typename
      ... on ResponseWithHoldingsAudienceExport {
        data {
          recipient
          exportId
          recipientCount
        }
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
`;

// One row per send, newest first.
export const GET_NOTIFICATION_AUDIENCE_SNAPSHOTS = gql`
  query NotificationAudienceSnapshots($notificationId: ID!) {
    getNotificationAudienceSnapshots(notificationId: $notificationId) {
      __typename
      ... on ResponseWithNotificationAudienceSnapshots {
        data {
          id
          resolvedAt
          recipientCount
          recipientsByChannel {
            channel
            count
          }
          instrumentTargeted
          asOf
          projectionStale
          criteria {
            operator
            assetClasses
            instruments {
              ticker
            }
            exchanges
            minHoldingValue {
              amount
              currency
            }
            includeUnsettled
          }
        }
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
`;
